import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";
import { currencySymbol } from "../utils/currency";
import { withQuietRetry } from "../utils/adminRetry";
import AdminLoadFailed from "./AdminLoadFailed";

// One row from GET /api/admin/orders/capture-failed — orders whose manual
// capture failed to collect the authorized funds (the issuer reversed the auth,
// the capture errored, or the PaymentIntent was no longer capturable). See
// lib/captureOrder.js on the server. `amount` is already in major units
// (dollars/euros), NOT Stripe minor units, so it renders directly.
interface CaptureFailedOrder {
  _id: string;
  amount: number;
  currency: string;
  dj: { _id: string; name: string; slug?: string; venueName?: string } | null;
  customerName: string | null;
  customerEmail: string | null;
  captureFailedAt: string | null;
  createdAt: string;
  status: string;
  paymentIntentId: string | null;
}

function fmtMoney(amount: number, currency?: string): string {
  const code = (currency || "usd").toUpperCase();
  return `${currencySymbol(currency)}${(amount || 0).toFixed(2)} ${code}`;
}

function fmtDateTime(d?: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function AdminPaymentIssuesTab() {
  const navigate = useNavigate();
  const [items, setItems] = useState<CaptureFailedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setFailed(false);
    withQuietRetry(() => adminApi.get("/orders/capture-failed"))
      .then((r) => setItems(r.data))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  // Total at risk, split per currency (amounts are already in major units).
  const totalsByCur = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const o of items) {
      const cur = (o.currency || "usd").toLowerCase();
      acc[cur] = (acc[cur] || 0) + (o.amount || 0);
    }
    return acc;
  }, [items]);

  if (loading) {
    return (
      <section className="fade-in">
        <p className="muted-text adm-center adm-p-40">Loading payment issues…</p>
      </section>
    );
  }

  return (
    <section className="fade-in">
      {/* What this tab is */}
      <div className="adm-card adm-mb-14" style={{ padding: "12px 16px" }}>
        <p className="muted-text adm-fs-80" style={{ margin: 0, lineHeight: 1.5 }}>
          Requests where the customer's card was authorized but the charge <strong>didn't clear</strong> when the
          song was played — usually the issuing bank reversed the hold (e.g. the customer never confirmed a fraud
          text). The song may already have been played, so this money likely wasn't collected. Newest first.
        </p>
      </div>

      {/* A list that didn't load is never "every played song collected cleanly". */}
      {failed ? (
        <AdminLoadFailed what="payment issues" onRetry={load} />
      ) : (
      <>
      {/* Stats */}
      <div className="adm-grid-5 adm-mb-14">
        <div className="kpi-card kpi-card-compact">
          <div>
            <div className="kpi-label">Payment Issues</div>
            <div className="kpi-value" style={items.length > 0 ? { color: "var(--adm-red)" } : {}}>{items.length}</div>
          </div>
        </div>
        {Object.entries(totalsByCur).map(([cur, total]) => (
          <div key={cur} className="kpi-card kpi-card-compact">
            <div>
              <div className="kpi-label">At Risk ({cur.toUpperCase()})</div>
              <div className="kpi-value adm-red">{fmtMoney(total, cur)}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="adm-card">
        {items.length === 0 ? (
          <p className="muted-text adm-center adm-p-30">
            No payment issues — every played song collected cleanly. 🎉
          </p>
        ) : (
          <div className="adm-scrollable-full" style={{ maxHeight: "calc(100vh - 360px)" }}>
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th>Failed</th>
                  <th>Amount</th>
                  <th>Talent</th>
                  <th>Customer</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const dj = o.dj;
                  return (
                    <tr key={o._id} className="hover-row">
                      <td className="adm-nowrap adm-fs-78">{fmtDateTime(o.captureFailedAt)}</td>
                      <td className="adm-bold">{fmtMoney(o.amount, o.currency)}</td>
                      <td>
                        {dj ? (
                          <div className="dj-cell dj-cell-compact clickable" onClick={() => navigate(`/admin/dj/${dj._id}`)}>
                            <div className="dj-cell-placeholder-sm">{dj.name?.[0]?.toUpperCase() || "?"}</div>
                            <span className="dj-link-text adm-fs-82">
                              {dj.name}{dj.venueName ? ` · ${dj.venueName}` : ""}
                            </span>
                          </div>
                        ) : (
                          <span className="muted-text">Unknown</span>
                        )}
                      </td>
                      <td className="adm-fs-78">{o.customerName || o.customerEmail || "—"}</td>
                      <td>
                        <span className="badge badge-sm badge-pending">{o.status}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </section>
  );
}
